import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { format, parseISO, addMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getTransactions } from '../services/api';
import { FaFilePdf, FaFilter, FaTrash, FaMoneyBill, FaCreditCard } from 'react-icons/fa';

const Reports = () => {
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Definindo os tipos de transação que o banco de dados espera
    const TransactionTypeDB = {
        Income: "entrada",
        Expense: "saida",
    };

    // Filtros - inicializar com ano atual e "Todos" para tipo de transação
    const currentYear = new Date().getFullYear();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [yearFilter, setYearFilter] = useState(currentYear.toString());
    const [monthFilter, setMonthFilter] = useState('');
    const [dayFilter, setDayFilter] = useState('');
    const [transactionTypeFilter, setTransactionTypeFilter] = useState('Todos'); // NOVO: Filtro por tipo de transação

    // Grupos de pagamento
    const paymentGroups = [
        'Todos',
        'Somente Dinheiro',
        'Pagamentos Digitais'
    ];
    const [selectedPaymentGroup, setSelectedPaymentGroup] = useState('Todos');

    // Tipos de pagamento individuais
    const paymentTypes = [
        'Todos',
        'Dinheiro',
        'Cartão de Crédito',
        'Cartão de Débito',
        'PIX',
        'Transferência Bancária'
    ];
    const [selectedPaymentType, setSelectedPaymentType] = useState('Todos');

    // Totais discriminados por tipo de transação
    const [totals, setTotals] = useState({
        totalIncome: 0,
        totalExpense: 0,
        cashIncome: 0,
        cashExpense: 0,
        digitalIncome: 0,
        digitalExpense: 0,
        balance: 0, // Saldo = totalIncome - totalExpense
    });

    // Efeito para buscar transações na montagem e ao navegar
    useEffect(() => {
        if (!localStorage.getItem("token")) {
            //Alert.alert("Você Não Tem Permissão Para Acessar a Pagina ")
            navigate("/login");
            return;
        }
        fetchTransactions();
    }, [navigate]);

    // Função para buscar todas as transações
    const fetchTransactions = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getTransactions();
            const data = Array.isArray(response.data) ? response.data : [];
            setTransactions(data);
            applyFilters(data); // Aplicar filtros iniciais após carregar
        } catch (err) {
            setError('Erro ao carregar transações');
            console.error(err);
            if (err.response?.status === 401) navigate("/login");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    // Função para aplicar os filtros e recalcular totais
    const applyFilters = useCallback((data = null) => {
        let baseData = data !== null ? data : transactions;
        if (!Array.isArray(baseData)) {
            baseData = [];
        }

        let filtered = [...baseData];

        // Filtro por intervalo de datas
        if (startDate && endDate) {
            const start = parseISO(startDate);
            const end = parseISO(endDate);
            filtered = filtered.filter(t => {
                const date = parseISO(t.data);
                return date >= start && date <= end;
            });
        }

        // Filtro por ano
        if (yearFilter && yearFilter !== "Todos") {
            filtered = filtered.filter(t =>
                new Date(t.data).getFullYear() === parseInt(yearFilter)
            );
        }

        // Filtro por mês
        if (monthFilter) {
            filtered = filtered.filter(t =>
                (new Date(t.data).getMonth() + 1) === parseInt(monthFilter)
            );
        }

        // Filtro por dia
        if (dayFilter) {
            filtered = filtered.filter(t =>
                new Date(t.data).getDate() === parseInt(dayFilter)
            );
        }

        // NOVO: Filtro por tipo de transação (Entrada/Saída)
        if (transactionTypeFilter !== 'Todos') {
            const dbType = transactionTypeFilter === 'Entrada' ? TransactionTypeDB.Income : TransactionTypeDB.Expense;
            filtered = filtered.filter(t => t.tipo === dbType);
        }

        // Filtro por grupo de pagamento
        if (selectedPaymentGroup !== 'Todos') {
            if (selectedPaymentGroup === 'Somente Dinheiro') {
                filtered = filtered.filter(t =>
                    t.forma_pagamento === 'Dinheiro'
                );
            } else if (selectedPaymentGroup === 'Pagamentos Digitais') {
                filtered = filtered.filter(t =>
                    ['Cartão de Crédito', 'Cartão de Débito', 'PIX', 'Transferência Bancária'].includes(t.forma_pagamento)
                );
            }
        }

        // Filtro por tipo de pagamento específico (só aplica se o grupo for "Todos")
        if (selectedPaymentGroup === 'Todos' && selectedPaymentType !== 'Todos') {
            filtered = filtered.filter(t =>
                t.forma_pagamento === selectedPaymentType
            );
        }

        setFilteredTransactions(filtered);
        calculateTotals(filtered);
    }, [startDate, endDate, yearFilter, monthFilter, dayFilter, transactionTypeFilter, selectedPaymentGroup, selectedPaymentType, transactions]);

    // Função para calcular os totais discriminados
    const calculateTotals = useCallback((transactionsToCalculate) => {
        let totalIncome = 0;
        let totalExpense = 0;
        let cashIncome = 0;
        let cashExpense = 0;
        let digitalIncome = 0;
        let digitalExpense = 0;

        transactionsToCalculate.forEach(t => {
            const value = parseFloat(t.valor) || 0;

            if (t.tipo === TransactionTypeDB.Income) {
                totalIncome += value;
                if (t.forma_pagamento === 'Dinheiro') {
                    cashIncome += value;
                } else {
                    digitalIncome += value;
                }
            } else if (t.tipo === TransactionTypeDB.Expense) {
                totalExpense += value;
                if (t.forma_pagamento === 'Dinheiro') {
                    cashExpense += value;
                } else {
                    digitalExpense += value;
                }
            }
        });

        setTotals({
            totalIncome,
            totalExpense,
            cashIncome,
            cashExpense,
            digitalIncome,
            digitalExpense,
            balance: totalIncome - totalExpense,
        });
    }, [TransactionTypeDB.Income, TransactionTypeDB.Expense]);

    // Efeito para re-aplicar filtros sempre que qualquer filtro mudar
    useEffect(() => {
        applyFilters();
    }, [startDate, endDate, yearFilter, monthFilter, dayFilter, transactionTypeFilter, selectedPaymentGroup, selectedPaymentType, applyFilters]);


    const resetFilters = useCallback(() => {
        setStartDate('');
        setEndDate('');
        setYearFilter("Todos");
        setMonthFilter('');
        setDayFilter('');
        setTransactionTypeFilter('Todos'); // Resetar o filtro de tipo de transação
        setSelectedPaymentGroup('Todos');
        setSelectedPaymentType('Todos');
        //applyFilters(transactions); // Com o useEffect, a chamada explícita aqui não é estritamente necessária
    }, []);

    // Função para gerar o PDF
    const generatePDF = () => {
        const doc = new jsPDF();

        const primaryColor = [41, 128, 185]; // Cor azul
        const incomeColor = [46, 204, 113]; // Verde
        const expenseColor = [231, 76, 60]; // Vermelho
        // const balanceColorPositive = [46, 204, 113]; // Não usado diretamente no PDF
        // const balanceColorNegative = [231, 76, 60]; // Não usado diretamente no PDF


        doc.setFontSize(16); // Título maior
        doc.setTextColor(...primaryColor);
        doc.setFont(undefined, 'bold');
        doc.text('RELATÓRIO FINANCEIRO', 105, 15, null, null, 'center');

        doc.setDrawColor(...primaryColor);
        doc.setLineWidth(0.5);
        doc.line(15, 20, 195, 20);

        const formatDateForDisplay = (dateString) => {
            if (!dateString) return '-';
            try {
                return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR });
            } catch (e) {
                console.error("Erro ao formatar data para PDF:", dateString, e);
                return dateString; // Retorna original se falhar
            }
        };

        let yPosition = 30;

        // Informações de filtros aplicados (compactadas)
        doc.setFontSize(10); // Fonte menor para filtros
        doc.setTextColor(40, 40, 40);
        doc.setFont(undefined, 'bold');
        doc.text('FILTROS APLICADOS:', 15, yPosition);
        yPosition += 5;

        doc.setFont(undefined, 'normal');
        let filtersText = `Período: ${startDate && endDate ? `${formatDateForDisplay(startDate)} à ${formatDateForDisplay(endDate)}` : 'Todos'} | `;
        filtersText += `Ano: ${yearFilter === "Todos" ? 'Todos' : yearFilter} | `;
        if (monthFilter) filtersText += `Mês: ${monthFilter} | `;
        if (dayFilter) filtersText += `Dia: ${dayFilter} | `;
        filtersText += `Transação: ${transactionTypeFilter} | `; // NOVO
        filtersText += `Grupo Pgto: ${selectedPaymentGroup} | `;
        if (selectedPaymentGroup === 'Todos' && selectedPaymentType !== 'Todos') {
            filtersText += `Tipo Pgto: ${selectedPaymentType}`;
        }
        doc.text(filtersText, 20, yPosition, { maxWidth: 180, align: 'left' }); // Limita largura
        yPosition += (Math.ceil(doc.getTextWidth(filtersText) / 180) * 4) + 5; // Ajusta altura da linha

        // RESUMO FINANCEIRO (Totais discriminados)
        doc.setFontSize(12); // Título de resumo
        doc.setFont(undefined, 'bold');
        doc.text('RESUMO FINANCEIRO', 15, yPosition);
        doc.setDrawColor(200, 200, 200);
        doc.line(15, yPosition + 2, 65, yPosition + 2); // Linha sublinhado
        yPosition += 8;

        doc.setFont(undefined, 'normal');
        const formatTotal = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

        doc.setTextColor(40, 40, 40);
        doc.text('Entradas Totais:', 20, yPosition); doc.setTextColor(...incomeColor); doc.text(formatTotal(totals.totalIncome), 65, yPosition);
        yPosition += 6;
        doc.setTextColor(40, 40, 40);
        doc.text('Saídas Totais:', 20, yPosition); doc.setTextColor(...expenseColor); doc.text(formatTotal(totals.totalExpense), 65, yPosition);
        yPosition += 6;
        doc.setTextColor(40, 40, 40);
        doc.text('Saldo Líquido:', 20, yPosition);
        // NOVO: Usar a função getBalanceColor para as cores do texto do saldo
        doc.setTextColor(...getPDFBalanceColor(totals.balance)); // Passa a cor como array RGB
        doc.text(formatTotal(totals.balance), 65, yPosition);
        yPosition += 6;

        doc.setTextColor(40, 40, 40);
        doc.text('Entradas em Dinheiro:', 20, yPosition); doc.setTextColor(...incomeColor); doc.text(formatTotal(totals.cashIncome), 65, yPosition);
        yPosition += 6;
        doc.setTextColor(40, 40, 40);
        doc.text('Saídas em Dinheiro:', 20, yPosition); doc.setTextColor(...expenseColor); doc.text(formatTotal(totals.cashExpense), 65, yPosition);
        yPosition += 6;

        doc.setTextColor(40, 40, 40);
        doc.text('Entradas Digitais:', 20, yPosition); doc.setTextColor(...incomeColor); doc.text(formatTotal(totals.digitalIncome), 65, yPosition);
        yPosition += 6;
        doc.setTextColor(40, 40, 40);
        doc.text('Saídas Digitais:', 20, yPosition); doc.setTextColor(...expenseColor); doc.text(formatTotal(totals.digitalExpense), 65, yPosition);
        yPosition += 6;

        doc.setTextColor(40, 40, 40);
        doc.text('Quantidade de Transações:', 20, yPosition);
        doc.text(filteredTransactions.length.toString(), 90, yPosition);
        yPosition += 10;

        // Dados das transações na tabela
        const tableData = filteredTransactions.map(t => {
            // Ajuste para offset de data/hora se necessário, como feito na Home
            const data = parseISO(t.data_atualizacao);
            const dataComOffset = addMinutes(data, 0); // Mantido 0 para consistência com outros lugares
            const dataFormatada = format(dataComOffset, "dd/MM/yyyy - HH:mm:ss", { locale: ptBR });

            return [
                t.descricao,
                dataFormatada,
                new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(t.valor),
                t.tipo === TransactionTypeDB.Income ? 'Entrada' : 'Saída', // Exibe 'Entrada' ou 'Saída'
                t.forma_pagamento,
                t.observacoes || '-'
            ];
        });

        const headers = [
            'Descrição',
            'Data',
            'Valor',
            'Tipo', // Coluna Tipo adicionada
            'Pagamento',
            'Observações'
        ];

        autoTable(doc, {
            startY: yPosition,
            head: [headers],
            body: tableData,
            theme: 'grid',
            styles: {
                fontSize: 8, // Fonte menor para a tabela
                cellPadding: 2, // Padding menor
                halign: 'left'
            },
            headStyles: {
                fillColor: primaryColor,
                textColor: 255,
                fontStyle: 'bold'
            },
            columnStyles: {
                0: { cellWidth: 35 }, // Descrição
                1: { cellWidth: 28 }, // Data
                2: { cellWidth: 22, halign: 'right' }, // Valor
                3: { cellWidth: 18 }, // Tipo (Entrada/Saída)
                4: { cellWidth: 28 }, // Pagamento
                5: { cellWidth: 35 } // Observações
            },
            didParseCell: function (data) {
                // Aplica cor ao valor na coluna 'Valor' e tipo na coluna 'Tipo'
                if (data.section === 'body') {
                    if (data.column.index === 2) { // Coluna 'Valor'
                        const originalTransaction = filteredTransactions[data.row.index];
                        if (originalTransaction) {
                            if (originalTransaction.tipo === TransactionTypeDB.Income) {
                                doc.setTextColor(...incomeColor);
                            } else if (originalTransaction.tipo === TransactionTypeDB.Expense) {
                                doc.setTextColor(...expenseColor);
                            }
                        }
                    }
                    if (data.column.index === 3) { // Coluna 'Tipo'
                        const originalTransaction = filteredTransactions[data.row.index];
                        if (originalTransaction) {
                            if (originalTransaction.tipo === TransactionTypeDB.Income) {
                                doc.setTextColor(...incomeColor);
                            } else if (originalTransaction.tipo === TransactionTypeDB.Expense) {
                                doc.setTextColor(...expenseColor);
                            }
                        }
                    }
                }
            }
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        const emissionDate = format(new Date(), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
        doc.setFontSize(9); // Fonte menor
        doc.setTextColor(100, 100, 100);
        doc.text(`Emitido em: ${emissionDate}`, 15, finalY);

        doc.save('relatorio-financeiro.pdf');
    };

    // Gerar lista de anos: 10 anos antes do atual até 10 anos depois
    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        years.push({ value: "Todos", label: "Todos" });
        for (let i = currentYear - 10; i <= currentYear + 10; i++) {
            years.push({ value: i.toString(), label: i.toString() });
        }
        return years;
    };

    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    const yearOptions = getYearOptions();

    // NOVO: Função para determinar a cor do saldo em formato RGB (para jsPDF)
    const getPDFBalanceColor = useCallback((balance) => {
        // Garante que o balance é um número para a comparação
        const numericBalance = Number(balance);
        return numericBalance >= 0 ? [46, 204, 113] : [231, 76, 60]; // Cores RGB para PDF
    }, []); // Dependência vazia, a função é estável.


    // Função para determinar a cor do saldo para HTML/CSS (retorna string)
    // Se você usa o getBalanceColor para o HTML também, ele deve retornar uma string CSS
    // Cuidado para não confundir os dois.
    const getHTMLBalanceColor = (balance) => {
        return balance >= 0 ? '#2ecc71' : '#e74c3c'; // Cores hexadecimais para CSS
    };

    return (
        <div>
            <Header />
            <div className="container" style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 15px' }}>

                {error && (
                    <div className="alert alert-danger" style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '10px', borderRadius: '4px', marginBottom: '15px', border: '1px solid #f5c6cb', marginTop: '50px', fontSize: '13px' }}>
                        {error}
                    </div>
                )}

                {/* Cards de Resumo */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px', marginBottom: '20px', marginTop: '40px' }}>
                    {/* Saldo Líquido */}
                    <div style={summaryCardStyle}>
                        <h3 style={summaryLabelStyle}>Saldo Líquido</h3>
                        <div style={{ fontSize: '1.6rem', fontWeight: '700', color: getHTMLBalanceColor(totals.balance), textAlign: 'center' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.balance)}
                        </div>
                    </div>
                    {/* Total Entradas */}
                    <div style={summaryCardStyle}>
                        <h3 style={summaryLabelStyle}>Total Entradas</h3>
                        <div style={{ fontSize: '1.6rem', fontWeight: '700', color: '#27ae60', textAlign: 'center' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.totalIncome)}
                        </div>
                    </div>
                    {/* Total Saídas */}
                    <div style={summaryCardStyle}>
                        <h3 style={summaryLabelStyle}>Total Saídas</h3>
                        <div style={{ fontSize: '1.6rem', fontWeight: '700', color: '#e74c3c', textAlign: 'center' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.totalExpense)}
                        </div>
                    </div>
                    {/* Entradas em Dinheiro */}
                    <div style={summaryCardStyle}>
                        <h3 style={summaryLabelStyle}>Dinheiro (Entradas)</h3>
                        <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#27ae60', textAlign: 'center' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.cashIncome)}
                        </div>
                    </div>
                    {/* Saídas em Dinheiro */}
                    <div style={summaryCardStyle}>
                        <h3 style={summaryLabelStyle}>Dinheiro (Saídas)</h3>
                        <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#e74c3c', textAlign: 'center' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.cashExpense)}
                        </div>
                    </div>
                    {/* Entradas Digitais */}
                    <div style={summaryCardStyle}>
                        <h3 style={summaryLabelStyle}>Digital (Entradas)</h3>
                        <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#2980b9', textAlign: 'center' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.digitalIncome)}
                        </div>
                    </div>
                    {/* Saídas Digitais */}
                    <div style={summaryCardStyle}>
                        <h3 style={summaryLabelStyle}>Digital (Saídas)</h3>
                        <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#e74c3c', textAlign: 'center' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.digitalExpense)}
                        </div>
                    </div>
                </div>

                {/* Filtros - Layout Horizontal Compacto */}
                <div style={filterCardStyle}>
                    <div style={filterCardHeaderStyle}>
                        <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.1rem' }}>
                            <FaFilter /> Filtros
                        </h5>
                    </div>
                    <div style={filterCardBodyStyle}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
                            {/* Data Inicial */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Data Inicial</label>
                                <input
                                    type="date"
                                    style={filterInputStyle}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>

                            {/* Data Final */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Data Final</label>
                                <input
                                    type="date"
                                    style={filterInputStyle}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>

                            {/* Ano */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Ano</label>
                                <select
                                    style={filterInputStyle}
                                    value={yearFilter}
                                    onChange={(e) => setYearFilter(e.target.value)}
                                >
                                    {yearOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Mês */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Mês</label>
                                <select
                                    style={filterInputStyle}
                                    value={monthFilter}
                                    onChange={(e) => setMonthFilter(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {months.map(month => (
                                        <option key={month} value={month}>
                                            {format(new Date(0, month - 1), 'MMM', { locale: ptBR })}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Dia */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Dia</label>
                                <select
                                    style={filterInputStyle}
                                    value={dayFilter}
                                    onChange={(e) => setDayFilter(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {days.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                            </div>

                            {/* NOVO: Tipo de Transação */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Tipo Transação</label>
                                <select
                                    style={filterInputStyle}
                                    value={transactionTypeFilter}
                                    onChange={(e) => setTransactionTypeFilter(e.target.value)}
                                >
                                    <option value="Todos">Todos</option>
                                    <option value="Entrada">Entrada</option>
                                    <option value="Saída">Saída</option>
                                </select>
                            </div>

                            {/* Grupo de Pagamento */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Grupo Pagamento</label>
                                <select
                                    style={filterInputStyle}
                                    value={selectedPaymentGroup}
                                    onChange={(e) => setSelectedPaymentGroup(e.target.value)}
                                >
                                    {paymentGroups.map(group => (
                                        <option key={group} value={group}>{group}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Tipo de Pagamento (depende do grupo "Todos") */}
                            <div style={filterGroupStyle}>
                                <label style={filterLabelStyle}>Tipo Pagamento</label>
                                <select
                                    style={filterInputStyle}
                                    value={selectedPaymentType}
                                    onChange={(e) => setSelectedPaymentType(e.target.value)}
                                    disabled={selectedPaymentGroup !== 'Todos'}
                                >
                                    {paymentTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Botões de Ação */}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                                <button
                                    style={filterButtonStyle}
                                    onClick={() => applyFilters()}
                                >
                                    <FaFilter size={12} /> Aplicar
                                </button>

                                <button
                                    style={{ ...filterButtonStyle, ...resetButtonStyle }}
                                    onClick={resetFilters}
                                >
                                    <FaTrash size={12} /> Limpar
                                </button>

                                <button
                                    style={{ ...filterButtonStyle, ...pdfButtonStyle }}
                                    onClick={generatePDF}
                                    disabled={filteredTransactions.length === 0}
                                >
                                    <FaFilePdf size={12} /> PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resultados da Tabela */}
                <div style={resultCardStyle}>
                    <div style={resultCardHeaderStyle}>
                        <h5 style={{ fontSize: '1.1rem', margin: 0 }}>Resultados</h5>
                        <span style={{ fontSize: '0.9rem' }}>
                            {filteredTransactions.length} {filteredTransactions.length === 1 ? 'transação encontrada' : 'transações encontradas'}
                        </span>
                    </div>

                    {loading ? (
                        <div style={loadingStateStyle}>
                            <p style={{ fontSize: '13px' }}>Carregando transações...</p>
                        </div>
                    ) : filteredTransactions.length === 0 ? (
                        <div style={noDataStyle}>
                            <p style={{ fontSize: '13px' }}>Nenhuma transação encontrada com os filtros selecionados.</p>
                        </div>
                    ) : (
                        <div style={{ padding: '0' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr style={tableHeaderRowStyle}>
                                            {/* Corrigido o problema de whitespace text nodes aqui: tags adjacentes */}
                                            <th style={tableHeaderCellStyle}>Descrição</th><th style={tableHeaderCellStyle}>Data</th><th style={tableHeaderCellStyle}>Valor</th><th style={tableHeaderCellStyle}>Tipo</th><th style={tableHeaderCellStyle}>Pagamento</th><th style={tableHeaderCellStyle}>Observações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTransactions.map(transaction => {
                                            const data = parseISO(transaction.data_atualizacao);
                                            const dataComOffset = addMinutes(data, 0);
                                            const dataFormatada = format(dataComOffset, "dd/MM/yyyy - HH:mm:ss", { locale: ptBR });

                                            return (
                                                <tr key={transaction.id} style={tableRowStyle}>
                                                    {/* Corrigido o problema de whitespace text nodes aqui: tags adjacentes */}
                                                    <td style={tableBodyCellStyle}>{transaction.descricao}</td><td style={tableBodyCellStyle}>{dataFormatada}</td><td style={{ ...tableBodyCellStyle, color: transaction.tipo === TransactionTypeDB.Income ? '#2ecc71' : '#e74c3c' }}>
                                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(transaction.valor)}
                                                    </td><td style={tableBodyCellStyle}>{transaction.tipo === TransactionTypeDB.Income ? 'Entrada' : 'Saída'}</td><td style={tableBodyCellStyle}>{transaction.forma_pagamento}</td><td style={tableBodyCellStyle}>{transaction.observacoes || '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr style={tableFooterRowStyle}>
                                            <td colSpan="2" style={tableFooterTotalLabelStyle}>Total Geral:</td>
                                            <td style={{ ...tableFooterTotalValueStyle, color: getHTMLBalanceColor(totals.balance) }}>
                                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.balance)}
                                            </td>
                                            <td colSpan="3" style={tableFooterCellStyle}></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- ESTILOS COMPACTOS PARA A PÁGINA DE RELATÓRIOS ---

const summaryCardStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    padding: '15px', // Padding menor
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px', // Espaçamento menor
    minHeight: '100px', // Altura mínima para cards
};

const summaryLabelStyle = {
    margin: '0',
    color: '#7f8c8d',
    fontSize: '0.9rem', // Fonte menor
    fontWeight: '600',
    textAlign: 'center',
};

const filterCardStyle = {
    border: 'none',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    marginBottom: '20px',
    borderRadius: '8px',
    backgroundColor: '#fff',
};

const filterCardHeaderStyle = {
    backgroundColor: '#3498db',
    color: 'white',
    padding: '12px 15px', // Padding menor
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
};

const filterCardBodyStyle = {
    padding: '15px', // Padding menor
};

const filterGroupStyle = {
    flex: '1',
    minWidth: '150px', // Largura mínima menor
};

const filterLabelStyle = {
    fontWeight: '600',
    color: '#555',
    fontSize: '0.8rem', // Fonte menor
    marginBottom: '4px', // Margem menor
    display: 'block',
};

const filterInputStyle = {
    padding: '7px 10px', // Padding menor
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '0.9rem', // Fonte menor
    width: '100%',
    boxSizing: 'border-box',
};

const filterButtonStyle = {
    padding: '8px 12px', // Padding menor
    display: 'flex',
    alignItems: 'center',
    gap: '6px', // Espaçamento menor
    height: '35px', // Altura menor
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem', // Fonte menor
    border: 'none',
    color: 'white',
    backgroundColor: '#3498db',
    transition: 'background-color 0.2s ease',
};

const resetButtonStyle = {
    backgroundColor: '#95a5a6',
};

const pdfButtonStyle = {
    backgroundColor: '#28a745',
};

const resultCardStyle = {
    border: 'none',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    backgroundColor: '#fff',
    marginBottom: '20px',
};

const resultCardHeaderStyle = {
    backgroundColor: '#2c3e50',
    color: 'white',
    padding: '12px 15px', // Padding menor
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.9rem', // Fonte menor
};

const loadingStateStyle = {
    padding: '30px', // Padding menor
    textAlign: 'center',
    color: '#555',
};

const noDataStyle = {
    padding: '30px', // Padding menor
    textAlign: 'center',
    color: '#777',
};

const tableStyle = {
    marginBottom: '0',
    borderCollapse: 'collapse',
    width: '100%',
    textAlign: 'left',
};

const tableHeaderRowStyle = {
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #ddd',
};

const tableHeaderCellStyle = {
    padding: '10px 12px', // Padding menor
    textAlign: 'left',
    fontSize: '0.8rem', // Fonte menor
    fontWeight: 'bold',
    color: '#555',
};

const tableRowStyle = {
    borderBottom: '1px solid #eee',
    // '&:hover': { backgroundColor: '#f8f9fa' } // CSS-in-JS não suporta pseudo-classes diretamente em inline styles
};

const tableBodyCellStyle = {
    padding: '8px 12px', // Padding menor
    fontSize: '0.8rem', // Fonte menor
    verticalAlign: 'middle',
};

const tableFooterRowStyle = {
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #ddd', // Linha para o rodapé
};

const tableFooterCellStyle = {
    padding: '10px 12px', // Padding menor
    fontSize: '0.8rem', // Fonte menor
};

const tableFooterTotalLabelStyle = {
    ...tableFooterCellStyle,
    textAlign: 'right',
    fontWeight: 'bold',
};

const tableFooterTotalValueStyle = {
    ...tableFooterCellStyle,
    fontWeight: 'bold',
    color: '#2c3e50', // Cor padrão, será sobrescrita pela cor do saldo
};

export default Reports;